import React, { useMemo } from 'react';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { GTFonts, useTheme } from '../../theme';
import { fs, s, v } from '../../utils/libs';
import StatCard, { type StatCardProps } from '../Reusables/StatCard';

type Props = {
  stats: StatCardProps[];
  comparisonCaption?: string | null;
  cardsPerColumn?: number;
  loading?: boolean;
};

const isWeb = Platform.OS === 'web';
const NATIVE_VISIBLE_COLUMNS = 2.25;

// Card i stays in column i / cardsPerColumn, row i % cardsPerColumn, but the
// cards are grouped by row so cards side by side stretch to the same height.
const toRows = (stats: StatCardProps[], cardsPerColumn: number) =>
  Array.from({ length: cardsPerColumn }, (_, rowIndex) =>
    stats.filter((_, index) => index % cardsPerColumn === rowIndex),
  );

const StatsCardsSection: React.FC<Props> = ({
  stats,
  // comparisonCaption,
  cardsPerColumn = 2,
  loading = false,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const { width } = useWindowDimensions();
  const rows = useMemo(
    () => toRows(stats, cardsPerColumn),
    [cardsPerColumn, stats],
  );

  if (isWeb) {
    return (
      <View style={styles.webGrid}>
        {/* {comparisonCaption ? (
          <Text style={styles.comparisonCaption}>{comparisonCaption}</Text>
        ) : null} */}
        {stats.map(stat => (
          <StatCard
            key={stat.label}
            {...stat}
            loading={loading || stat.loading}
          />
        ))}
      </View>
    );
  }

  const columnGap = s(10);
  const horizontalInset = s(16);
  const columnWidth = Math.max(
    s(142),
    Math.floor((width - horizontalInset) / NATIVE_VISIBLE_COLUMNS),
  );

  return (
    <View style={styles.nativeSection}>
      {/* {comparisonCaption ? (
        <Text style={styles.comparisonCaption}>{comparisonCaption}</Text>
      ) : null} */}
      <ScrollView
        horizontal
        nestedScrollEnabled
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.nativeScrollContent}>
        <View style={styles.nativeColumn}>
          {rows.map(row => (
            <View
              key={row.map(stat => stat.label).join('-')}
              style={styles.nativeRow}>
              {row.map((stat, columnIndex) => (
                <StatCard
                  key={stat.label}
                  {...stat}
                  loading={loading || stat.loading}
                  containerStyle={[
                    styles.nativeCard,
                    {
                      width: columnWidth,
                      maxWidth: columnWidth,
                      marginRight:
                        columnIndex === row.length - 1 ? 0 : columnGap,
                    },
                  ]}
                />
              ))}
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
};

export default StatsCardsSection;

const createStyles = (theme: any) =>
  StyleSheet.create({
    webGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      rowGap: v(10),
      margin: v(8),
    },
    nativeSection: {
      marginTop: v(10),
    },
    nativeScrollContent: {
      paddingHorizontal: s(10),
      paddingBottom: v(2),
    },
    nativeColumn: {
      rowGap: v(10),
    },
    nativeRow: {
      flexDirection: 'row',
    },
    nativeCard: {
      width: '100%',
      maxWidth: '100%',
      flexBasis: 'auto',
    },
    comparisonCaption: {
      width: '100%',
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      marginBottom: v(6),
      paddingHorizontal: s(8),
    },
  });
